import type { Outputs } from "@postdaily/api/routers/index";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button, buttonVariants } from "@postdaily/ui/components/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@postdaily/ui/components/empty";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { createFileRoute, Link } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useQueryState } from "nuqs";
import { toast } from "sonner";
import { ChannelManageList } from "@/components/channels/channel-manage-list";
import { PageHeader } from "@/components/page-header";
import { useConnectChannelDialog } from "@/components/social-accounts/connect-channel-dialog";
import { useConnectProvider } from "@/hooks/use-connect-provider";
import { useConnectReturn } from "@/hooks/use-connect-return";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { POST_DIALOG_PARAM, postDialogParser } from "@/lib/post-dialog-param";
import { takeReconnectReturnPost } from "@/lib/reconnect-return-post";
import { useUpgradeDialogStore } from "@/stores/upgrade-dialog-store";

export const Route = createFileRoute("/(app)/channels/")({
  component: RouteComponent,
});

type Workspace = NonNullable<
  ReturnType<typeof useCurrentWorkspace>["currentWorkspace"]
>;

function RouteComponent() {
  const { currentWorkspace } = useCurrentWorkspace();

  if (!currentWorkspace) {
    return <Skeleton className="h-72 w-full" />;
  }

  return <ChannelsManage workspace={currentWorkspace} />;
}

function ChannelsManage({ workspace }: { workspace: Workspace }) {
  const openUpgradeDialog = useUpgradeDialogStore(
    (store) => store.openUpgradeDialog,
  );
  const {
    accountsQuery,
    billingState,
    invalidateAccountData,
    isPending,
    isError,
    isAtLimit,
    limits,
    planName,
  } = useConnectProvider({ workspace });

  const [, setOpenPostId] = useQueryState(POST_DIALOG_PARAM, postDialogParser);

  const connectDialog = useConnectChannelDialog({
    workspace,
    onConnected: async () => {
      await invalidateAccountData();
      // Reconnecting from a post's LinkedIn comments panel lands here to
      // re-pick the Page; offer the way back to that post.
      const postId = takeReconnectReturnPost();
      if (postId) {
        toast.success("Channel reconnected", {
          action: {
            label: "Back to post",
            onClick: () => {
              void setOpenPostId(postId);
            },
          },
          duration: 15_000,
        });
      }
    },
  });

  // OAuth providers redirect back here with a status flag.
  useConnectReturn(connectDialog.openStep);

  const accounts: Outputs["socialAccounts"]["list"] = accountsQuery.data ?? [];
  const connectedCount =
    billingState.data?.usage?.socialAccountsCount ?? accounts.length;
  const maxChannels = limits?.maxSocialAccounts;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Channels"
        description="Manage your connected channels and their posting schedules."
        actions={
          <Link
            to="/channels/connect"
            className={buttonVariants({ size: "sm" })}
          >
            <PlusIcon data-icon="inline-start" />
            Connect channel
          </Link>
        }
      />

      {!isPending && !isError ? (
        <p className="text-muted-foreground text-sm">
          {connectedCount}
          {maxChannels ? `/${maxChannels}` : ""} channel
          {connectedCount === 1 ? "" : "s"} connected
        </p>
      ) : null}

      {isAtLimit ? (
        <Alert>
          <AlertTitle>Channel limit reached</AlertTitle>
          <AlertDescription>
            {planName} includes {maxChannels} channels. Upgrade to connect more.
          </AlertDescription>
          <AlertAction>
            <Button
              onClick={() => openUpgradeDialog({ source: "channel_limit" })}
              size="sm"
              type="button"
              variant="outline"
            >
              Upgrade
            </Button>
          </AlertAction>
        </Alert>
      ) : null}

      {isError ? (
        <Alert variant="destructive">
          <AlertTitle>Channels unavailable</AlertTitle>
          <AlertDescription>{accountsQuery.error?.message}</AlertDescription>
        </Alert>
      ) : null}

      {isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : accounts.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No channels connected</EmptyTitle>
            <EmptyDescription>
              Connect a social account to start scheduling and publishing.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Link
              to="/channels/connect"
              className={buttonVariants({ size: "sm" })}
            >
              <PlusIcon data-icon="inline-start" />
              Connect channel
            </Link>
          </EmptyContent>
        </Empty>
      ) : (
        <ChannelManageList workspace={workspace} accounts={accounts} />
      )}

      {connectDialog.dialog}
    </div>
  );
}
